import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { readReportShareToken } from '@api/reports';
import { readSurveyShareToken } from '@api/surveys';
import { Announcement, CopyToClipboard, Input, LoaderSkeleton } from '@utilities';

import styles from './_index.module.scss';

const Public = ({ id, isSurvey }) => {
  const { tab_id: tabId } = useParams();
  const [error, setError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shareToken, setShareToken] = useState(null);

  useEffect(() => {
    const getShareToken = async () => {
      setIsLoading(true);
      try {
        const response = isSurvey
          ? await readSurveyShareToken({ surveyId: id })
          : await readReportShareToken({ reportId: id });
        setShareToken(response.data);
        setIsLoading(false);
      } catch (err) {
        setError(err);
        console.warn(error);
      }
    };
    getShareToken();
  }, [id, isSurvey, setShareToken]);

  const publicURL = isSurvey
    ? `${window.location.origin}/share/survey/${shareToken}/${id}/${tabId && tabId !== 'whats-next' ? tabId : ''}`
    : `${window.location.origin}/share/report/${shareToken}/${tabId && tabId !== 'whats-next' ? tabId : ''}`;

  if (error) {
    return (
      <Announcement
        text="There has been a problem creating the public share link. Please refresh the page and try again."
        variant="warn"
      />
    );
  }

  if (isLoading) {
    return (
      <LoaderSkeleton height={150}>
        <rect height="90" rx="4" ry="4" width="1333" x="0" y="0" />
        <rect height="40" rx="4" ry="4" width="1333" x="0" y="120" />
      </LoaderSkeleton>
    );
  }

  return (
    <div className={styles['public']}>
      <Announcement
        text={
          isSurvey
            ? 'Copy this link to share the survey report with those who do not have a Numerator Verified Voices account. They will be able to see the data shared here and modify filters.'
            : 'Copy this report link to share with those who do not have a Numerator Insights account. They will be able to see the data shared here but will not be able to re-prompt and run reports.'
        }
        variant="info"
      />
      <span>
        <Input data-testid="public-input" isDisabled value={publicURL} />
        <CopyToClipboard data-testid="public-copy-to-clipboard-button" value={publicURL} />
      </span>
    </div>
  );
};

Public.propTypes = {
  id: PropTypes.number,
  isSurvey: PropTypes.bool,
};

Public.defaultProps = {
  isSurvey: false,
};

export default Public;
